import React from "react";
import { TextProps } from "@chakra-ui/react"; //eslint-disable-line
import Markdown from "react-markdown";
import remarkGfm from 'remark-gfm'
import {
	Box, Button,
	Link,
	ListItem,
	OrderedList,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text, Th,
	Thead,
	Tr,
	UnorderedList
} from "~/foundation/Chakra";
import { Icon } from "../Icon";
import { useTranslation } from "~/foundation/Dictionary";
import type { SearchDictionary } from "~/feature/Search/dictionary.ts"; //eslint-disable-line
import type { AliaChatQuestion } from "~/foundation/SitecoreContent/generated-types.ts";

type ChatMarkdownProps = {
	children: string;
	formatMessageQuestions?: boolean;
	handleQuestion?: (question: AliaChatQuestion) => void;
	chatLogId?: string;
	noOfLines?: TextProps['noOfLines'];
	removeMargin?: boolean;
	centerTables?: boolean;
	tableSpacingTop?: number;
	tableSpacingBottom?: number;
	paragraphsMargin?: number;
};

function ChatMarkdown(props: ChatMarkdownProps) {
	const [t] = useTranslation<SearchDictionary>();
	const markDownProps = props;

	// If noOfLines is sent, only render first element - used to truncate the response in SearchPageChatBot
	// We only truncate the paragraphs
	const content = markDownProps.noOfLines
		? props.children.split('\n\n')[0]
		: props.children;

	const { centerTables, tableSpacingTop = 0, tableSpacingBottom = 0, paragraphsMargin = 2 } = markDownProps;
	const margin = markDownProps.removeMargin ? 0 : paragraphsMargin;

	return <Markdown remarkPlugins={[remarkGfm]} components={{
		p(props) {
			return <Text my={margin} noOfLines={markDownProps.noOfLines}>{props.children}</Text>
		},
		a(props) {
			return <Link color="primary.aramcoLinkBlue" {...props}>{props.children}</Link>
		},
		ol(props) {
			return <OrderedList>{props.children}</OrderedList>
		},
		ul(props) {
			return <UnorderedList>{props.children}</UnorderedList>
		},
		li(props) {
			const question = props.children?.toString().trim();
			const hasQuestionMark = question?.endsWith('?') || question?.endsWith('؟') || question?.startsWith('؟');
			const addAliaButton = markDownProps.formatMessageQuestions && hasQuestionMark && question && typeof markDownProps.handleQuestion === "function";

			if (addAliaButton){
				return (
					<ListItem my={4} ms="-1rem" listStyleType="none" display="grid" gridTemplateColumns="auto max-content" gap={4}>
						<Box>{question}</Box>
						<Button
							variant="outline"
							size="md"
							color="primary.darkBlue"
							borderColor="currentcolor"
							h="fit-content"
							alignSelf="center"
							fontSize=".75rem"
							lineHeight=".75rem"
							p="0 .25rem 0 .5rem"
							onClick={() => {
								if (markDownProps.handleQuestion && markDownProps.chatLogId){
									markDownProps.handleQuestion({ question: question, chatlogId: markDownProps.chatLogId })
								}
							}}>
							<span>{t("search.askAlia")}</span>
							<Icon
								color="currentColor"
								variant="Alia"
								ms=".5rem"
								w="1.5rem"
								position="relative"
								zIndex={1}
							/>
						</Button>
					</ListItem>
				)
			} else {
				return <ListItem my={2}>{props.children}</ListItem>
			}
		},
		table(props) {
			return (
				<TableContainer
					mt={tableSpacingTop}
					mb={tableSpacingBottom}
					{...(centerTables && {
						sx: {
							marginInline: "auto",
							maxWidth: ["100%", null, null, "53.75rem"],
							width: "100%"
						}
					})}>
					<Table variant="striped" size="sm" my={2} minW="full">{props.children}</Table>
				</TableContainer>)
		},
		thead(props) {
			return <Thead borderBottom="1px solid" borderColor="primary.lightGrey">{props.children}</Thead>
		},
		tbody(props) {
			return <Tbody>{props.children}</Tbody>
		},
		th(props) {
			return <Th px={1} pb={1} textAlign="start">{props.children}</Th>
		},
		tr(props) {
			return <Tr _notLast={{ borderBottom: "1px solid", borderColor: "primary.lightGrey" }}>{props.children}</Tr>
		},
		td(props) {
			return <Td p={1} whiteSpace="pre-wrap">{props.children}</Td>
		}
	}}>
		{content}
	</Markdown>
}

export default ChatMarkdown;